import type {
  AjustesTenant, Cotizacion, Id, Maquina, Material, Parametro, Programa, RegistroTiempo, SolicitudCotizacion,
} from '@laser/core';
import type {
  AgruparReporte, CambiosAjustes, CambiosTrabajo, ClienteConResumen, ClienteDetalle, EventoTrabajo,
  FilaReporte, FiltrosApiTrabajos, PaginaTrabajos, Precision, TrabajoDetalle, TrabajoResumen, Yo,
} from './tipos';
import { transporteHttp, type Peticion, type Transporte } from './transporte';

const g = <T>(t: Transporte, ruta: string, query?: Peticion['query']) => t({ metodo: 'GET', ruta, query }) as Promise<T>;
const env = <T>(t: Transporte, metodo: Peticion['metodo'], ruta: string, cuerpo?: unknown) =>
  t({ metodo, ruta, cuerpo: cuerpo ?? {} }) as Promise<T>;
const enc = encodeURIComponent;

export function crearApi(t: Transporte) {
  const crud = <T extends { id: Id }>(base: string) => ({
    listar: () => g<T[]>(t, base),
    crear: (datos: Omit<T, 'id' | 'tenantId'>) => env<T>(t, 'POST', base, datos),
    actualizar: (id: Id, cambios: Partial<Omit<T, 'id' | 'tenantId'>>) => env<T>(t, 'PATCH', `${base}/${enc(id)}`, cambios),
    eliminar: (id: Id) => t({ metodo: 'DELETE', ruta: `${base}/${enc(id)}` }) as Promise<unknown>,
  });

  return {
    yo: () => g<Yo>(t, '/yo'),

    trabajos: {
      async listar(f: FiltrosApiTrabajos = {}): Promise<PaginaTrabajos> {
        const r = await g<PaginaTrabajos | TrabajoResumen[]>(t, '/trabajos', { ...f });
        return Array.isArray(r) ? { trabajos: r, siguienteCursor: null } : { ...r, siguienteCursor: r.siguienteCursor ?? null };
      },
      async obtener(id: Id): Promise<TrabajoDetalle> {
        const r = await g<TrabajoDetalle>(t, `/trabajos/${enc(id)}`);
        return { ...r, archivos: r.archivos ?? [] };
      },
      actualizar: (id: Id, cambios: CambiosTrabajo) => env<TrabajoDetalle>(t, 'PATCH', `/trabajos/${enc(id)}`, cambios),
      recotizar: (id: Id, solicitud: SolicitudCotizacion) => env<Cotizacion>(t, 'POST', `/trabajos/${enc(id)}/recotizar`, solicitud),
      aprobar: (id: Id) => env<unknown>(t, 'POST', `/trabajos/${enc(id)}/aprobar`),
      iniciarTiempo: (id: Id, datos: { maquinaId: Id; nota?: string }) => env<RegistroTiempo>(t, 'POST', `/trabajos/${enc(id)}/tiempo/iniciar`, datos),
      detenerTiempo: (id: Id, datos: { maquinaId: Id; nota?: string }) => env<RegistroTiempo>(t, 'POST', `/trabajos/${enc(id)}/tiempo/detener`, datos),
      tiempo: (id: Id) => g<RegistroTiempo[]>(t, `/trabajos/${enc(id)}/tiempo`),
      eventos: (id: Id) => g<EventoTrabajo[]>(t, `/trabajos/${enc(id)}/eventos`),
    },

    maquinas: crud<Maquina>('/maquinas'),
    materiales: crud<Material>('/materiales'),
    parametros: {
      listar: () => g<Parametro[]>(t, '/parametros'),
      /** Alta o cambio de la combinación máquina × material. */
      guardar: (p: Parametro) => env<Parametro>(t, 'PUT', `/parametros/${enc(p.maquinaId)}/${enc(p.materialId)}`, p),
      eliminar: (maquinaId: Id, materialId: Id) => t({ metodo: 'DELETE', ruta: `/parametros/${enc(maquinaId)}/${enc(materialId)}` }),
    },

    ajustes: {
      obtener: () => g<AjustesTenant>(t, '/ajustes'),
      actualizar: (cambios: CambiosAjustes) => env<AjustesTenant>(t, 'PATCH', '/ajustes', cambios),
    },

    clientes: {
      listar: (q?: string) => g<ClienteConResumen[]>(t, '/clientes', { q }),
      obtener: (id: Id) => g<ClienteDetalle>(t, `/clientes/${enc(id)}`),
    },

    programa: () => g<Programa>(t, '/programa'),
    reportes: (desde: string, hasta: string, agrupar: AgruparReporte) => g<FilaReporte[]>(t, '/reportes', { desde, hasta, agrupar }),
    precision: (n?: number) => g<Precision>(t, '/precision', { n }),

    archivos: {
      original: (id: Id) => t({ metodo: 'GET', ruta: `/archivos/${enc(id)}/original`, respuesta: 'blob' }) as Promise<Blob>,
    },
    /** Texto de una URL servida por la api (SVG normalizado). */
    texto: (url: string) => t({ metodo: 'GET', ruta: '', url, respuesta: 'texto' }) as Promise<string>,
  };
}

export type Api = ReturnType<typeof crearApi>;

// `import.meta.env.VITE_MOCK` va en línea para que Vite lo reemplace y el build real no incluya el mock.
const transporte: Transporte = import.meta.env.VITE_MOCK === '1' || import.meta.env.VITE_MOCK === 'true'
  ? async (p) => (await import('../mock/servidor')).transporteMock(p)
  : transporteHttp;

export const api = crearApi(transporte);
